import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuthStore } from '@store/authStore';

interface AdminManagementProps {
  academyId: string;
}

const AdminManagement: React.FC<AdminManagementProps> = ({ academyId }) => {
  const { user } = useAuthStore();
  const [admins, setAdmins] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<any>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    isSuperAdmin: false,
  });

  // Check if the admin being edited is the current user and is a superadmin
  const isEditingCurrentSuperAdmin = editingAdmin && 
    user?.id && 
    (editingAdmin.id === user.id || editingAdmin.userId === user.id) &&
    (editingAdmin.isSuperAdmin === true || editingAdmin.is_super_admin === true);

  useEffect(() => {
    if (academyId) {
      loadAdmins();
    } else {
      console.error('[AdminManagement] Academy ID is undefined');
    }
  }, [academyId]);

  const loadAdmins = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/academies/${academyId}/admins`);
      const result = await response.json();
      
      if (result.success) {
        const adminsData = (result.data || []).map((admin: any) => ({
          ...admin,
          isSuperAdmin: admin.isSuperAdmin === true || admin.is_super_admin === true
        }));
        console.log('[AdminManagement] Loaded admins:', adminsData);
        setAdmins(adminsData);
      } else {
        setError(result.message || 'Failed to load admins');
      }
    } catch (err: any) {
      console.error('Error loading admins:', err);
      setError('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    console.log('[AdminManagement] handleAddAdmin called', { editingAdmin, formData });
    
    if (!formData.email || !formData.firstName || !formData.lastName) {
      console.log('[AdminManagement] Validation failed - missing required fields');
      toast.error('Please fill in all required fields');
      return;
    }

    if (!editingAdmin && !formData.password) {
      console.log('[AdminManagement] Validation failed - password required for new admin');
      toast.error('Password is required for new admins');
      return;
    }

    console.log('[AdminManagement] Starting API call...');
    setLoading(true);
    try {
      const url = editingAdmin 
        ? `/api/academies/${academyId}/admins/${editingAdmin.id}`
        : `/api/academies/${academyId}/admins`;
      
      const requestBody = editingAdmin ? {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password || undefined,
        isSuperAdmin: formData.isSuperAdmin,
      } : {
        ...formData,
        isSuperAdmin: formData.isSuperAdmin,
      };
      
      console.log('[AdminManagement] Making request:', {
        url,
        method: editingAdmin ? 'PUT' : 'POST',
        body: requestBody
      });
      
      const response = await fetch(url, {
        method: editingAdmin ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('[AdminManagement] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AdminManagement] Response error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('[AdminManagement] Response data:', result);

      if (result.success) {
        toast.success(editingAdmin ? 'Admin updated successfully' : 'Academy admin created successfully');
        setDialogOpen(false);
        setEditingAdmin(null);
        setFormData({ email: '', password: '', firstName: '', lastName: '', isSuperAdmin: false });
        loadAdmins();
      } else {
        toast.error(result.message || `Failed to ${editingAdmin ? 'update' : 'add'} admin`);
      }
    } catch (err: any) {
      console.error('[AdminManagement] Error saving admin:', err);
      toast.error(`Failed to ${editingAdmin ? 'update' : 'add'} admin: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAdmin = (admin: any) => {
    console.log('[AdminManagement] handleEditAdmin called with:', admin);
    setEditingAdmin(admin);
    const isSuperAdmin = admin.isSuperAdmin === true || admin.is_super_admin === true;
    setFormData({
      email: admin.email || '',
      password: '', // Don't pre-fill password
      firstName: admin.firstName || admin.first_name || '',
      lastName: admin.lastName || admin.last_name || '',
      isSuperAdmin: isSuperAdmin,
    });
    console.log('[AdminManagement] Form data set to:', {
      email: admin.email || '',
      firstName: admin.firstName || admin.first_name || '',
      lastName: admin.lastName || admin.last_name || '',
      isSuperAdmin: isSuperAdmin,
    });
    setDialogOpen(true);
  };

  const handleDeleteAdmin = async (adminId: string) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/academies/${academyId}/admins/${adminId}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Admin deleted successfully');
        loadAdmins();
      } else {
        toast.error(result.message || 'Failed to delete admin');
      }
    } catch (err: any) {
      console.error('Error deleting admin:', err);
      toast.error('Failed to delete admin');
    } finally {
      setLoading(false);
    }
  };

  if (loading && admins.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Admin Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Add Admin
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Super Admin</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No admins found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    {`${admin.firstName || ''} ${admin.lastName || ''}`}
                  </TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>
                    {(admin.isSuperAdmin === true || admin.is_super_admin === true) ? (
                      <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                        Yes
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleEditAdmin(admin)}
                      title="Edit Admin"
                    >
                      <EditIcon />
                    </IconButton>
                    {!(admin.isSuperAdmin === true || admin.is_super_admin === true) && (
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteAdmin(admin.id)}
                        title="Delete Admin"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditingAdmin(null); setFormData({ email: '', password: '', firstName: '', lastName: '', isSuperAdmin: false }); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAdmin ? 'Edit Academy Admin' : 'Add Academy Admin'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="First Name *"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Last Name *"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email *"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            {!editingAdmin && (
              <TextField
                fullWidth
                label="Password *"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isSuperAdmin}
                  onChange={(e) => setFormData({ ...formData, isSuperAdmin: e.target.checked })}
                  color="primary"
                  disabled={isEditingCurrentSuperAdmin}
                />
              }
              label="Super Admin"
            />
            {isEditingCurrentSuperAdmin && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                You cannot depromote yourself from Super Admin role. The user who registered during academy creation must remain a Super Admin.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('[AdminManagement] Update button clicked');
              handleAddAdmin();
            }} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? (editingAdmin ? 'Updating...' : 'Adding...') : (editingAdmin ? 'Update Admin' : 'Add Admin')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagement;

